// ScheduleTimeline.js
import React, { useMemo, useState, useEffect } from 'react';
import { VStack, Collapse } from '@chakra-ui/react';
import './components/timeline.group.css';
import './rainbow.css';
// capacityForCount removed from header display logic; kept in util for ratio if needed
import dayjs from 'dayjs';
import ScheduleItem from './components/ScheduleItem';
import ScheduleGroupHeader from './components/ScheduleGroupHeader';
import { groupSchedulesByTimeframe } from './components/utils/groupSchedulesByTimeframe';

const ScheduleTimeline = ({ schedules = [], refreshSchedules, editingTarget, completingTarget, automationDisabled = false, recoveryMessage = 'Ekkos is in recovery mode.' }) => {
  const groups = useMemo(() => groupSchedulesByTimeframe(schedules, dayjs()), [schedules]);

  // Open state: open the 'active'/'now' group if present; otherwise open the first group.
  const [open, setOpen] = useState(() => {
    const init = {};
    if (!groups || groups.length === 0) return init;
    const hasNow = groups.some(g => g.key === 'active' || g.key === 'now');
    for (const [i, g] of groups.entries()) {
      if (hasNow) init[g.key] = (g.key === 'active' || g.key === 'now');
      else init[g.key] = i === 0; // open first group by default
    }
    return init;
  });

  useEffect(() => {
    // When groups change, keep user's manual open/close choices where possible,
    // but ensure 'Now' is open if present; otherwise ensure the first visible group is open.
    setOpen(prev => {
      const next = {};
      const hasNow = groups.some(g => g.key === 'active' || g.key === 'now');
      for (const [i, g] of groups.entries()) {
        const was = prev[g.key];
        if (hasNow) next[g.key] = (g.key === 'active' || g.key === 'now') || (typeof was === 'boolean' ? was : false);
        else next[g.key] = typeof was === 'boolean' ? was : (i === 0);
      }
      return next;
    });
  }, [groups]);

  return (
    <VStack align="stretch" spacing={3} w="100%" minW="0">
      {groups.map(group => (
        <div
          key={group.key}
          data-key={group.key}
          className="ek-group"
          // tier attribute removed — no weight label or coloring
        >
          <ScheduleGroupHeader
            label={group.label}
            count={group.items.length}
            isOpen={!!open[group.key]}
            onToggle={() => setOpen(s => ({ ...s, [group.key]: !s[group.key] }))}
          />
          <Collapse in={!!open[group.key]} animateOpacity unmountOnExit={false}>
            <div className="ek-group__body">
            <VStack align="stretch" spacing={3} mt={1} w="100%" minW="0">
              {group.items.map((item, idx) => (
                <ScheduleItem
                  key={item.originJobKey + item.nextRunTime}
                  item={item}
                  refreshSchedules={refreshSchedules}
                  editingTarget={editingTarget}
                  completingTarget={completingTarget}
                  onEditIntent={(it) => {
                    if (typeof window?.setGlobalEditingTarget === 'function') window.setGlobalEditingTarget(it);
                  }}
                  onCompleteIntent={(it) => {
                    if (typeof window?.setGlobalCompletingTarget === 'function') window.setGlobalCompletingTarget(it);
                  }}
                  automationDisabled={automationDisabled}
                  recoveryMessage={recoveryMessage}
                />
              ))}
            </VStack>
            </div>
          </Collapse>
        </div>
      ))}
    </VStack>
  );
};

export default ScheduleTimeline;
